import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { API, formatDateBR } from '../App';
import { Alert, AlertDescription } from './ui/alert';
import { Loader2, Truck, Package, CheckCircle, Clock, MapPin, X, ZoomIn, ChevronLeft, ChevronRight } from 'lucide-react';
import { colors } from '../theme';

const RastreioPublico = ({ tipo }) => {
  const { codigo, nfe, identificador: paramId } = useParams();
  const [loading, setLoading] = useState(true);
  const [dados, setDados] = useState(null);
  const [erro, setErro] = useState(null);
  const [fotoAberta, setFotoAberta] = useState(null); // índice da foto em tela cheia

  // Determinar o identificador correto baseado na rota
  const identificador = paramId || nfe || codigo;

  useEffect(() => {
    if (identificador) buscarRastreamento();
  }, [identificador]);

  const buscarRastreamento = async () => {
    setLoading(true);
    setErro(null);
    
    try {
      // Endpoint universal - tenta buscar por NF, pedido ou código de rastreamento
      const endpoint = `${API}/rastreio-publico/consulta/${identificador}`;
      
      const response = await axios.get(endpoint);
      setDados(response.data);
    } catch (error) {
      console.error('Erro ao buscar rastreamento:', error);
      setErro(error.response?.data?.detail || 'Erro ao buscar informações de rastreamento');
    } finally {
      setLoading(false);
    }
  };

  // Deduplicar fotos do comprovante (evitar fotos duplicadas da API)
  // IMPORTANT: This hook must be called before any early returns to comply with React rules of hooks
  const fotosUnicas = React.useMemo(() => {
    const fotos = dados?.comprovante_entrega?.fotos || [];
    const seen = new Set();
    return fotos.filter(f => {
      const key = typeof f === 'string' ? f.substring(0, 100) : '';
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [dados?.comprovante_entrega?.fotos]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-xl p-8 text-center max-w-sm">
          <Loader2 className="h-12 w-12 animate-spin mb-3 mx-auto" style={{ color: colors.primaza.primary }} />
          <p className="text-gray-600 font-medium">Buscando rastreamento...</p>
          <p className="text-gray-400 text-xs mt-1">Aguarde um momento</p>
        </div>
      </div>
    );
  }

  if (erro) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-xl p-6 max-w-sm">
          <div className="text-center mb-4">
            <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3" style={{ backgroundColor: colors.primaza.primary + '20' }}>
              <Package className="h-6 w-6" style={{ color: colors.primaza.primary }} />
            </div>
            <h2 className="text-xl font-bold text-gray-800 mb-2">Ops!</h2>
          </div>
          <Alert variant="destructive">
            <AlertDescription className="text-center text-sm">{erro}</AlertDescription>
          </Alert>
          <button
            onClick={buscarRastreamento}
            className="mt-4 w-full text-white font-medium py-2.5 px-4 rounded-lg transition-colors text-sm"
            style={{ backgroundColor: colors.primaza.primary }}
          >
            Tentar Novamente
          </button>
        </div>
      </div>
    );
  }

  const rastreamento = dados?.rastreamento;

  // Função para formatar data de evento
  const formatarDataEvento = (evento) => {
    if (evento.hora && evento.data && !evento.data.includes('T')) {
      const dataFormatada = evento.data.split('-').reverse().join('/');
      return `${dataFormatada} ${evento.hora}`;
    }
    if (evento.data && evento.data.includes('T')) {
      const dt = new Date(evento.data);
      return dt.toLocaleDateString('pt-BR') + ' ' + dt.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    }
    return formatDateBR(evento.data);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-4 px-3">
      <div className="max-w-3xl mx-auto">
        {/* Header com Logo Primaza */}
        <div className="bg-white rounded-xl shadow-xl overflow-hidden mb-4">
          <div className="px-4 py-5 text-white" style={{ background: `linear-gradient(135deg, ${colors.primaza.primary} 0%, ${colors.primaza.secondary} 100%)` }}>
            {/* Logo Primaza */}
            <div className="text-center mb-4">
              <img 
                src="/logo-primaza.png" 
                alt="Primaza" 
                className="h-12 mx-auto mb-2 bg-white rounded-lg px-3 py-2"
              />
              <p className="text-white/90 text-xs font-medium">Rastreamento Animal</p>
            </div>
            
            <div className="flex items-center gap-3 mb-3">
              <div className="bg-white/20 p-2 rounded-lg">
                <Truck size={24} />
              </div>
              <div>
                <h1 className="text-xl font-bold">Acompanhe Seu Pedido</h1>
                <p className="text-white/90 text-sm mt-0.5">Rastreabilidade do início ao fim: do brinco até você</p>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
              <div className="bg-white/10 rounded-lg p-3 backdrop-blur-sm">
                <p className="text-white/80 text-xs mb-0.5">
                  {dados.codigo_correios ? 'Nota Fiscal' : 'Código de Rastreamento'}
                </p>
                <p className="font-bold text-base">
                  {dados.codigo_correios ? (dados.nfe || dados.codigo_rastreamento) : dados.codigo_rastreamento}
                </p>
              </div>
              {dados.pedido_numero && (
                <div className="bg-white/10 rounded-lg p-3 backdrop-blur-sm">
                  <p className="text-white/80 text-xs mb-0.5">Número do Pedido</p>
                  <p className="font-bold text-base">#{dados.pedido_numero}</p>
                </div>
              )}
              {dados.previsao_entrega && !rastreamento?.entregue && (
                <div className="bg-white/10 rounded-lg p-3 backdrop-blur-sm">
                  <p className="text-white/80 text-xs mb-0.5">Previsão de Entrega</p>
                  <p className="font-bold text-base">{formatDateBR(dados.previsao_entrega)}</p>
                </div>
              )}
              {dados.codigo_correios && (
                <div className="rounded-lg p-3 backdrop-blur-sm" style={{ backgroundColor: 'rgba(249,115,22,0.25)', border: '1.5px solid rgba(249,115,22,0.6)' }}>
                  <p className="text-xs mb-0.5 font-semibold" style={{ color: '#fed7aa' }}>Código Rastreio Correios</p>
                  <p className="font-bold text-base tracking-widest font-mono" style={{ color: '#fff' }}>{dados.codigo_correios}</p>
                </div>
              )}
            </div>
          </div>

          {/* Status Atual */}
          <div className="p-4">
            <div className={`rounded-lg p-4 border-2 ${
              rastreamento?.entregue 
                ? 'bg-green-50 border-green-200' 
                : 'bg-blue-50 border-blue-200'
            }`}>
              <div className="flex items-center gap-3">
                <div className={`p-3 rounded-lg ${
                  rastreamento?.entregue ? 'bg-green-100' : 'bg-blue-100'
                }`}>
                  {rastreamento?.entregue ? (
                    <CheckCircle className="h-7 w-7 text-green-600" />
                  ) : (
                    <Package className="h-7 w-7 text-blue-600" />
                  )}
                </div>
                <div className="flex-1">
                  <p className={`font-bold text-base mb-1 ${
                    rastreamento?.entregue ? 'text-green-700' : 'text-blue-700'
                  }`}>
                    {rastreamento?.ultimo_status || 'Aguardando atualização'}
                  </p>
                  <div className="flex flex-wrap items-center gap-3 text-xs text-gray-600">
                    {rastreamento?.ultima_data && (
                      <span className="flex items-center gap-1">
                        <Clock size={14} />
                        <span className="font-medium">{formatDateBR(rastreamento.ultima_data)}</span>
                      </span>
                    )}
                    {rastreamento?.ultima_localizacao && (
                      <span className="flex items-center gap-1">
                        <MapPin size={14} />
                        <span className="font-medium">{rastreamento.ultima_localizacao}</span>
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Histórico de Eventos */}
        {rastreamento?.eventos && rastreamento.eventos.length > 0 && (
          <div className="bg-white rounded-xl shadow-xl p-4">
            <div className="flex items-center gap-2 mb-4">
              <div className="p-2 rounded-lg bg-gray-100">
                <Clock size={18} className="text-gray-600" />
              </div>
              <div>
                <h2 className="font-bold text-gray-800 text-base">Histórico de Movimentação</h2>
                <p className="text-gray-500 text-xs">{rastreamento.eventos.length} eventos</p>
              </div>
            </div>
            
            <div className="relative">
              {/* Linha vertical suave */}
              <div className="absolute left-[19px] top-4 bottom-4 w-px bg-gray-300"></div>
              
              <div className="space-y-3">
                {rastreamento.eventos.map((evento, index) => {
                  const isEntregue = evento.status === 'ENTREGUE' || 
                    (evento.descricao && evento.descricao.toLowerCase().includes('entreg'));
                  
                  return (
                    <div key={index} className="relative pl-12">
                      {/* Ícone do evento */}
                      <div className={`absolute left-0 top-0 p-2 rounded-lg ${
                        index === 0 
                          ? 'bg-green-100 ring-2 ring-green-200' 
                          : 'bg-gray-100 ring-2 ring-gray-200'
                      }`}>
                        {index === 0 ? (
                          <CheckCircle size={16} style={{ color: colors.primaza.primary }} />
                        ) : isEntregue ? (
                          <CheckCircle size={16} className="text-green-600" />
                        ) : (
                          <Truck size={16} className="text-gray-600" />
                        )}
                      </div>
                      
                      {/* Conteúdo do evento */}
                      <div className={`rounded-lg p-3 ${
                        index === 0 
                          ? 'bg-green-50 border-2 border-green-200' 
                          : 'bg-white border border-gray-200 hover:border-gray-300 transition-colors'
                      }`}>
                        <p className={`font-semibold text-sm mb-2 ${
                          index === 0 ? 'text-green-800' : 'text-gray-800'
                        }`}>
                          {evento.descricao}
                        </p>
                        <div className="flex flex-wrap items-center gap-2">
                          {(evento.data || evento.hora) && (
                            <span className="flex items-center gap-1.5 bg-gray-50 px-2 py-1 rounded-md text-xs">
                              <Clock size={12} className="text-gray-500" />
                              <span className="font-medium text-gray-700">
                                {formatarDataEvento(evento)}
                              </span>
                            </span>
                          )}
                          {evento.localizacao && (
                            <span className="flex items-center gap-1.5 bg-gray-50 px-2 py-1 rounded-md text-xs">
                              <MapPin size={12} className="text-gray-500" />
                              <span className="font-medium text-gray-700">{evento.localizacao}</span>
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Footer */}
            <div className="mt-6 pt-4 border-t text-center">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                <span>🐄</span>
                <span>Primaza Rastreamento Animal</span>
              </div>
              <p className="text-gray-400 text-xs mt-2">
                Atualizado em: {formatDateBR(dados.compartilhado_em)}
              </p>
            </div>
          </div>
        )}

        {/* Sem eventos */}
        {(!rastreamento?.eventos || rastreamento.eventos.length === 0) && (
          <div className="bg-white rounded-xl shadow-xl p-6 text-center">
            <Package className="h-12 w-12 mx-auto mb-3 text-gray-400" />
            <p className="text-gray-600 font-medium">Aguardando movimentação</p>
            <p className="text-gray-400 text-sm mt-1">Os eventos aparecerão aqui assim que disponíveis</p>
          </div>
        )}

        {/* Comprovante de Entrega */}
        {dados?.comprovante_entrega && rastreamento?.entregue && (
          <div className="bg-white rounded-xl shadow-xl overflow-hidden mt-4">
            <div className="bg-gradient-to-r from-green-600 to-emerald-700 text-white px-4 py-3">
              <div className="flex items-center gap-2">
                <CheckCircle size={20} />
                <h2 className="font-bold text-base">Comprovante de Entrega</h2>
              </div>
            </div>
            <div className="p-4 space-y-4">
              {/* Fotos do comprovante */}
              {fotosUnicas.length > 0 && (
                <div>
                  <p className="text-sm font-semibold text-gray-700 mb-2">
                    Foto{fotosUnicas.length > 1 ? 's' : ''} da Entrega
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {fotosUnicas.map((foto, idx) => (
                      <div
                        key={idx}
                        className="rounded-lg overflow-hidden border-2 border-gray-200 shadow-sm cursor-pointer relative group"
                        onClick={() => setFotoAberta(idx)}
                        data-testid={`comprovante-foto-${idx}`}
                      >
                        <img
                          src={foto.startsWith('data:') ? foto : `data:image/jpeg;base64,${foto}`}
                          alt={`Comprovante ${idx + 1}`}
                          className="w-full h-auto object-cover"
                        />
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all flex items-center justify-center">
                          <ZoomIn size={32} className="text-white opacity-0 group-hover:opacity-100 transition-opacity drop-shadow-lg" />
                        </div>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-gray-400 mt-2 text-center">Toque na foto para ampliar</p>
                </div>
              )}

              {/* Localização GPS */}
              {dados.comprovante_entrega.latitude && dados.comprovante_entrega.longitude && (
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-1.5">
                    <MapPin size={14} className="text-green-600" />
                    Localização da Entrega
                  </p>
                  <a
                    href={`https://www.google.com/maps?q=${dados.comprovante_entrega.latitude},${dados.comprovante_entrega.longitude}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-sm text-green-700 font-medium hover:text-green-800 underline"
                    data-testid="comprovante-mapa-link"
                  >
                    <MapPin size={14} />
                    Ver no Google Maps
                  </a>
                  <p className="text-xs text-gray-500 mt-1">
                    GPS: {Number(dados.comprovante_entrega.latitude).toFixed(6)}, {Number(dados.comprovante_entrega.longitude).toFixed(6)}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Lightbox - Foto em tela cheia */}
        {fotoAberta !== null && fotosUnicas.length > 0 && (
          <div
            className="fixed inset-0 bg-black/90 z-50 flex flex-col items-center justify-center"
            onClick={() => setFotoAberta(null)}
            data-testid="foto-lightbox"
          >
            {/* Botão fechar */}
            <button
              className="absolute top-4 right-4 text-white/80 hover:text-white bg-black/40 rounded-full p-2 z-10"
              onClick={() => setFotoAberta(null)}
              data-testid="lightbox-close"
            >
              <X size={28} />
            </button>

            {/* Contador */}
            <p className="absolute top-5 left-1/2 -translate-x-1/2 text-white/70 text-sm font-medium">
              {fotoAberta + 1} / {fotosUnicas.length}
            </p>

            {/* Navegação */}
            {fotosUnicas.length > 1 && (
              <>
                <button
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-white/70 hover:text-white bg-black/40 rounded-full p-2"
                  onClick={(e) => {
                    e.stopPropagation();
                    setFotoAberta((fotoAberta - 1 + fotosUnicas.length) % fotosUnicas.length);
                  }}
                  data-testid="lightbox-prev"
                >
                  <ChevronLeft size={32} />
                </button>
                <button
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-white/70 hover:text-white bg-black/40 rounded-full p-2"
                  onClick={(e) => {
                    e.stopPropagation();
                    setFotoAberta((fotoAberta + 1) % fotosUnicas.length);
                  }}
                  data-testid="lightbox-next"
                >
                  <ChevronRight size={32} />
                </button>
              </>
            )}

            {/* Imagem em tela cheia com pinch-to-zoom */}
            <div
              className="max-w-[95vw] max-h-[85vh] overflow-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={(() => {
                  const f = fotosUnicas[fotoAberta];
                  return f.startsWith('data:') ? f : `data:image/jpeg;base64,${f}`;
                })()}
                alt={`Comprovante ${fotoAberta + 1}`}
                className="max-w-none w-auto h-auto"
                style={{ maxHeight: '85vh', touchAction: 'pinch-zoom' }}
                data-testid="lightbox-image"
              />
            </div>
          </div>
        )}

        {/* Informações Adicionais */}
        {(dados.transportadora || dados.previsao_entrega) && (
          <div className="bg-white rounded-xl shadow-lg p-4 mt-4">
            <h3 className="font-bold text-gray-800 mb-3 text-sm">Informações da Entrega</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {dados.transportadora && (
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-gray-500 text-xs mb-0.5">Transportadora</p>
                  <p className="font-semibold text-gray-800 text-sm">{dados.transportadora}</p>
                </div>
              )}
              {dados.previsao_entrega && (
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-gray-500 text-xs mb-0.5">Previsão de Entrega</p>
                  <p className="font-semibold text-gray-800 text-sm">{formatDateBR(dados.previsao_entrega)}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Botão Atualizar */}
        <div className="text-center mt-4">
          <button
            onClick={buscarRastreamento}
            className="text-white font-medium py-2.5 px-6 rounded-lg transition-all shadow-md hover:shadow-lg inline-flex items-center gap-2 text-sm"
            style={{ backgroundColor: colors.primaza.primary }}
            onMouseEnter={(e) => e.target.style.backgroundColor = colors.primaza.secondary}
            onMouseLeave={(e) => e.target.style.backgroundColor = colors.primaza.primary}
          >
            <Clock size={18} />
            Atualizar Informações
          </button>
        </div>
      </div>
    </div>
  );
};

export default RastreioPublico;
